/* eslint-disable no-console, no-continue */
import { Lightning } from '@lightningjs/sdk';
import { backToBuffer, cleanupBuffer, getFromBuffer, PAGE_STATE } from '../../../../utils/virtualization';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, PORTION_REQUESTED_SIGNAL, SELECTED_SIGNAL } from '../../../../signals';
import { EpgBox } from '../epgBox/epgBox.component';
import { GAP as EPG_BOX_GAP } from '../epgBox/epgBox.style';
import {
  ARROW_SLIDE_INTERVAL,
  CHANNEL_SIZE,
  defaultContext,
  GAP,
  MAX_VISIBLE_ROWS,
  RIGHT_PADDING,
  SCROLL_START_ROW,
} from '../epg.style';
import {
  ITEM_FOCUS_TIMEOUT_MS as ITEM_FOCUS_TIMEOUT,
  MAX_UPDATES_PER_FRAME,
  SET_X_SIGNAL,
  SET_Y_SIGNAL,
} from './epgGrid.constants';
import { PIXELS_PER_MINUTE } from '../epg.constants';

export class EpgGrid extends Lightning.Component {
  static _template() {
    return {
      clipping: true,
      Container: {},
    };
  }

  _construct() {
    this._style = defaultContext;
    this._resetVirtualization();
  }

  _firstActive() {
    this._step = this._animationLoop.bind(this);
    this.stage.on('frameStart', this._step);
  }

  _attach() {
    this._isGridAttached = true;
  }

  _detach() {
    this.stage.removeListener('frameStart', this._step);
    this._step = null;
    clearTimeout(this._itemFocusTimeout);
    this._isGridAttached = false;
    this._resetVirtualization();
  }

  set rows(value) {
    if (!value) {
      return;
    }

    const { id, rows, pages, pagination, rowHeight, start, end } = value;

    if ((this._start ? this._start.getTime() : 0) !== start.getTime()) {
      console.log('[EPG] New rows set, removing current views');

      this.tag('Container').children.forEach(view => {
        this._backToBuffer(view);
      });
    } else if (this._blockId !== id) {
      this._refreshRequired = true;
    }

    const { totalItems, itemsPerPage } = pagination;
    this._blockId = id;
    this._totalItems = totalItems;
    this._itemsPerPage = itemsPerPage;
    this._pages = pages;
    this._rows = rows;
    this._rowsCount = rows.length;
    this._rowHeight = rowHeight;
    this._start = start;
    this._end = end;
    this._limits = rows.map(({ children }) => {
      const lastChild = children[children.length - 1];

      return lastChild.x + lastChild.w;
    });

    for (let i = 0; i < this._rowsCount; i += 1) {
      if (!this._viewsByRows[i]) {
        this._viewsByRows[i] = [];
      }
    }
  }

  get rightPadding() {
    return RIGHT_PADDING[this._style.screenSize];
  }

  get rightmostOffset() {
    return this.finalW - (this._limits[this._focusedRowIndex] || 0) - this.rightPadding;
  }

  goToTimestamp(value, skipAnimation = false, alignToCenter = true) {
    const container = this.tag('Container');
    const offset = alignToCenter ? Math.round(this.finalW / 2) : 0;
    if (value < this._start || value > this._end) {
      this._focusedItemIndex = this._findNearestToPointInRow(offset - container.x, this._focusedRowIndex);

      return;
    }

    const timeOffset = (value - this._start) / (this._end - this._start);
    const maxOffset = PIXELS_PER_MINUTE * 24 * 60;
    const center = maxOffset * timeOffset;
    const targetPosition = Math.max(Math.min(0, Math.round(offset - center)), this.rightmostOffset);

    if (skipAnimation) {
      container.x = targetPosition;
    } else {
      container.setSmooth('x', targetPosition);
    }
    this._focusedItemIndex = this._findNearestToPointInRow(center, this._focusedRowIndex);
    this.signal(SET_X_SIGNAL, targetPosition, this.rightmostOffset);
  }

  goLeft() {
    const container = this.tag('Container');
    const target = Math.min(container.x + ARROW_SLIDE_INTERVAL[this._style.screenSize], 0);
    container.setSmooth('x', target);
    this._focusedItemIndex = this._findNearestToPointInRow(-target, this._focusedRowIndex);
    this.signal(SET_X_SIGNAL, target, this.rightmostOffset);
  }

  goRight() {
    const container = this.tag('Container');
    const maxOffset = this.rightmostOffset;
    const target = Math.max(container.x - ARROW_SLIDE_INTERVAL[this._style.screenSize], maxOffset);
    container.setSmooth('x', target);
    this._focusedItemIndex = this._findNearestToPointInRow(-target, this._focusedRowIndex);
    this.signal(SET_X_SIGNAL, target, maxOffset);
  }

  _onViewBecomeInactive(view) {
    if (this._isGridAttached) {
      this._backToBuffer(view);
    }
  }

  _onViewBecomeDetached(view) {
    cleanupBuffer(view);
  }

  _getViewFromBufferOrCreate() {
    const template = EpgBox.templates.EpgBox;
    const existingView = getFromBuffer(template);

    if (existingView) {
      return existingView;
    }

    return this.stage.c({
      type: EpgBox,
      template,
      passSignals: {
        // [SELECTED_SIGNAL]: true,
      },
      signals: {
        [INACTIVE_SIGNAL]: '_onViewBecomeInactive',
        [DETACHED_SIGNAL]: '_onViewBecomeDetached',
      },
    });
  }

  _backToBuffer(view) {
    if (!view) {
      return;
    }

    delete this._views[view.index];
    if (this._viewsByRows[view.rowIndex]) {
      this._viewsByRows[view.rowIndex] = this._viewsByRows[view.rowIndex].filter(entry => entry !== view);
    }
    backToBuffer(view);
    // view.alpha = 0;
    view.visible = false;

    this._renderList = this._renderList.filter(entry => entry !== view);
  }

  _switchToCurrentRow(fromView) {
    this._focusedItemIndex = this._findNearestIndex(fromView, this._focusedRowIndex);

    const maxVisibleRows = MAX_VISIBLE_ROWS[this._style.screenSize];
    const rows = Math.max(0, Math.min(this._focusedRowIndex - SCROLL_START_ROW + 1, this._rowsCount - maxVisibleRows));
    const offset = -rows * (CHANNEL_SIZE[this._style.screenSize] + GAP);
    const container = this.tag('Container');
    container.setSmooth('y', offset);
    this.signal(SET_Y_SIGNAL, offset);
    // In case a right/left-most is focused, scroll horizontally to the edge
    this._goTo(this._focusedItemIndex, false, true);

    this._startItemFocusTimeout();

    if (this._itemsPerPage > 0 && this._pages) {
      const currentPage = Math.floor(this._focusedRowIndex / this._itemsPerPage);
      // NOTE: the first page will be empty in case if the Row is used within a Selector block
      const nextPage = this._pages[0] === PAGE_STATE.LOADED ? currentPage + 1 : 0;

      if (currentPage !== nextPage) {
        this._requestPage(currentPage);
        // In case the row was restored with anchoring from a backward navigation
        if (currentPage - 1 >= 0) {
          this._requestPage(currentPage - 1);
        }
      }

      this._requestPage(nextPage);
    }
  }

  /**
   * Sends a signal requesting a specific page of items
   *
   * @param {Number} page zero-based page index
   */
  _requestPage(page) {
    if (this._pages[page] !== PAGE_STATE.MISSING) {
      return;
    }

    this.signal(PORTION_REQUESTED_SIGNAL, {
      // NOTE: BFF API requires the pages to be 1-based
      page: page + 1,
      start: page * this._itemsPerPage,
      id: this._blockId,
      date: this._start,
    });
  }

  _findNearestIndex(view, rowIndex) {
    let start = this._verticalTraverseStartPoint || (view ? view.offset : 0);

    if (!this._verticalTraverseStartPoint) {
      this._verticalTraverseStartPoint = start;
    }

    start = Math.max(start, -this.tag('Container').x);

    return this._findNearestToPointInRow(start, rowIndex);
  }

  _findNearestToPointInRow(start, rowIndex) {
    const candidates = this._rows[rowIndex].children;

    for (let i = 0; i < candidates.length; i += 1) {
      const candidate = candidates[i];
      if (candidate.x >= start) {
        return candidate.itemIndex;
      }

      if (candidate.x + candidate.w > start) {
        return candidate.itemIndex;
      }
    }

    return candidates.length - 1;
  }

  _animationLoop() {
    if (!this._rows) {
      return;
    }

    const container = this.tag('Container');
    const paddingX = Math.round(this.finalW / 4);
    const paddingY = this._rowHeight;
    const left = Math.max(-container.x - paddingX, 0);
    const top = Math.max(-container.y - paddingY, 0);
    const right = this.finalW + paddingX - container.x;
    const bottom = this.finalH + paddingY - container.y;
    const toRender = [];

    // container.children.forEach(view => {
    //   if (view.y + view.h < top || view.y > bottom || view.x + view.w < left || view.x > right) {
    //     this._backToBuffer(view);
    //   }
    // });

    for (let row = 0; row < this._rows.length; row += 1) {
      const { y, h, children } = this._rows[row];

      if (y + h < top) {
        continue;
      }

      if (y > bottom) {
        break;
      }

      for (let i = 0; i < children.length; i += 1) {
        const child = children[i];
        const { x, w } = child;

        if (x + w < left) {
          continue;
        }

        if (x > right) {
          break;
        }

        if (this._views[child.index]) {
          this._updateInnerOffset(this._views[child.index], -container.x, true);
          if (this._refreshRequired) {
            toRender.push(child);
          }
        } else {
          toRender.push(child);
        }
      }
    }

    const updatesCount = Math.min(toRender.length, MAX_UPDATES_PER_FRAME);

    for (let i = 0; i < updatesCount; i += 1) {
      const data = toRender[i];
      let view = this._views[data.index];
      if (!view) {
        view = this._getViewFromBufferOrCreate();
        this._renderList.push(view);

        if (!this._viewsByRows[data.rowIndex]) {
          this._viewsByRows[data.rowIndex] = [];
        }
        this._viewsByRows[data.rowIndex].push(view);
      }

      if (view.index !== data.index || !view.visible || this._refreshRequired) {
        view.rowIndex = data.rowIndex;
        view.itemIndex = data.itemIndex;

        view.patch({
          x: data.x,
          y: data.y,
          item: data.item,
          index: data.index,
          visible: true,
        });
      }

      if (view.parent !== container) {
        container.childList.add(view);
      }

      this._views[data.index] = view;
    }

    if (container.x !== this._previousX || container.y !== this._previousY || updatesCount) {
      this._refocus();
      this._previousX = container.x;
      this._previousY = container.y;
      this._isMoving = true;
    } else if (this._isMoving) {
      this._isMoving = false;
    }

    this._refreshRequired = false;
  }

  _updateInnerOffsets(fringe, skipAnimation = false) {
    const views = this._renderList;
    for (let i = 0; i < views.length; i += 1) {
      this._updateInnerOffset(views[i], fringe, skipAnimation);
    }
  }

  _updateInnerOffset(view, fringe, skipAnimation = false) {
    const value =
      view.offset >= fringe || view.offset + view.itemWidth >= fringe
        ? Math.max(fringe - view.offset + view.padding, view.minInnerOffset)
        : view.minInnerOffset;

    if (skipAnimation) {
      view.innerOffset = value;
    } else {
      view.setSmooth('innerOffset', value);
    }
  }

  _goTo(index, skipAnimation = false, keepHorizontalBounds = false) {
    clearTimeout(this._itemFocusTimeout);

    const rowIndex = this._focusedRowIndex;
    const row = this._rows ? this._rows[rowIndex] : null;
    if (!row) {
      return;
    }

    const box = row.children[index];
    if (!box) {
      return;
    }

    const container = this.tag('Container');
    const viewRight = box.item.offset + box.item.itemWidth;
    const containerLeft = container.x;
    const containerRight = this.finalW - this.rightPadding;
    // Positive offset along the same X line boxes are placed upon
    const containerRightOffset = containerRight - containerLeft;
    const maxOffset = this.rightmostOffset;
    const view = this._views[box.index];
    const viewWidth =
      keepHorizontalBounds || !view || !view.expandedWidth ? 0 : Math.min(view.collapsedWidth, view.expandedWidth);

    if (box.x + viewWidth > containerRightOffset || (!keepHorizontalBounds && index === row.children.length - 1)) {
      const offset = containerRight - viewRight;
      const constrainedOffset = Math.max(offset, maxOffset);

      if (!skipAnimation) {
        container.setSmooth('x', constrainedOffset);
        this.signal(SET_X_SIGNAL, constrainedOffset, maxOffset);
      }
    } else if (box.x + box.w + containerLeft <= EPG_BOX_GAP || (!keepHorizontalBounds && index === 0)) {
      const offset = -box.item.offset;

      if (!skipAnimation) {
        container.setSmooth('x', offset);
        this.signal(SET_X_SIGNAL, offset, maxOffset);
      }
    }

    this._startItemFocusTimeout();
  }

  _startItemFocusTimeout() {
    clearTimeout(this._itemFocusTimeout);

    this._itemFocusTimeout = setTimeout(() => {
      const view = this._getFocused();
      const right = this.finalW - this.rightPadding;
      const left = this.tag('Container').x;

      const expandedWidth = Math.max(view.expandedWidth, Math.max(1, view.collapsedWidth));
      const viewRight = view.offset + expandedWidth;
      const itemWidth = expandedWidth + Math.max(0, view.innerOffset - (expandedWidth - view.expandedWidth));

      if (viewRight > right - left) {
        const overlap = view.offset + view.expandedWidth - this.finalW + left;
        view.x = Math.min(view.offset - overlap, view.offset - (itemWidth - view.collapsedWidth));
      }
      view.currentWidth = itemWidth;
    }, ITEM_FOCUS_TIMEOUT);
  }

  _handleLeft() {
    if (this._focusedItemIndex > 0) {
      this._focusedItemIndex -= 1;
      this._verticalTraverseStartPoint = undefined;
      this._goTo(this._focusedItemIndex);

      return true;
    }

    if (this._verticalTraverseStartPoint) {
      this._verticalTraverseStartPoint = undefined;
      this._goTo(this._focusedItemIndex);
    }

    return false;
  }

  _handleRight() {
    const row = this._rows ? this._rows[this._focusedRowIndex] : null;

    if (!row) {
      return false;
    }

    if (this._focusedItemIndex < row.children.length - 1) {
      this._focusedItemIndex += 1;
      this._verticalTraverseStartPoint = undefined;
      this._goTo(this._focusedItemIndex);

      return true;
    }

    if (this._verticalTraverseStartPoint) {
      this._verticalTraverseStartPoint = undefined;
      this._goTo(this._focusedItemIndex);
    }

    return false;
  }

  _handleUp() {
    if (this._focusedRowIndex > 0) {
      const currentFocusedView = this._getFocused();
      this._focusedRowIndex -= 1;
      this._switchToCurrentRow(currentFocusedView);

      return true;
    }

    return false;
  }

  _handleDown() {
    if (this._focusedRowIndex < this._rowsCount - 1) {
      const currentFocusedView = this._getFocused();
      this._focusedRowIndex += 1;
      this._switchToCurrentRow(currentFocusedView);

      return true;
    }

    return false;
  }

  _handleEnter() {
    const box = this._getFocused();

    this.signal(SELECTED_SIGNAL, box && box.item ? box.item.content : null);

    return true;
  }

  _getFocused() {
    return this._views[`${this._focusedRowIndex}_${this._focusedItemIndex}`];
  }

  _focus() {
    this._startItemFocusTimeout();
  }

  _unfocus() {
    clearTimeout(this._itemFocusTimeout);
    this._verticalTraverseStartPoint = undefined;
  }

  _resetVirtualization() {
    this._verticalTraverseStartPoint = undefined;
    this._tickTime = null;
    this._views = {};
    this._viewsByRows = {};
    this._renderList = [];
    this._limits = [];
    this._focusedRowIndex = 0;
    this._focusedItemIndex = 0;
    this._previousX = null;
    this._previousY = null;
  }
}
